import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import defaultAlbumThumbnail from '../../assets/default_album_thumbnail.svg';
import { Link } from 'react-router-dom';
import theme from '../../theme';
import ArtistLink from '../common/ArtistLink';
import AlbumTitleLink from '../common/AlbumTitleLink';
import { useUserStore } from '../../stores/userStore';
import { trackListContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, LIST_ACTION_TYPE } from '../../constants/gaEnums';

const PlaylistAlbumCard = ({
  albumImageUrl,
  albumTitle,
  artistName,
  releaseDate,
  genreNames = [],
  contentRating = 'CLEAN',
  rank,
  actions,
  albumId,
  albumSourceId,
  albumSource,
  albumSlug,
  artistSource,
  artistSourceId,
  artistSlug,
  artistDbId,
  referrerSection,
}) => {
  const [imageSrc, setImageSrc] = useState(albumImageUrl || defaultAlbumThumbnail);
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const albumUrl = albumSlug
    ? `/reviews/${albumSlug}`
    : albumSourceId
      ? `/reviews/${albumSource?.toLowerCase()}/${albumSourceId}`
      : `/reviews/local/${albumId}`;
  const albumLinkState = referrerSection ? { referrerSection } : undefined;

  useEffect(() => {
    setImageSrc(albumImageUrl || defaultAlbumThumbnail);
  }, [albumImageUrl]);

  const handleError = () => {
    setImageSrc(defaultAlbumThumbnail);
  };

  const trackAlbumClick = () => {
    trackListContentClick(
      {
        contentType: CONTENT_TYPE.LIST_ALBUM,
        actionType: LIST_ACTION_TYPE.ALBUM_CLICK,
        contentId: `album_${albumId || albumSourceId}`,
        contentName: albumTitle,
      },
      user,
    );
  };

  const handleAlbumClick = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      trackAlbumClick();
      navigateWithScrollMemory(albumUrl, albumLinkState ? { state: albumLinkState } : undefined);
    }
  };

  const handleArtistClick = () => {
    trackListContentClick(
      {
        contentType: CONTENT_TYPE.LIST_ALBUM,
        actionType: LIST_ACTION_TYPE.ARTIST_CLICK,
        contentId: `artist_${artistSourceId}`,
        contentName: artistName,
      },
      user,
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 2 },
        width: '100%',
        px: { xs: 2, md: 2, lg: 2.5 },
        py: { xs: 2, md: 2.5, lg: 2.5 },
        bgcolor: theme.palette.grey.bg,
      }}
    >
      {/* 1. 순서 */}
      {rank && (
        <Typography
          sx={{
            width: { xs: 10, md: 20 },
            textAlign: 'left',
            flexShrink: 0,
            fontWeight: 700,
            fontSize: { xs: 12, md: 13 },
          }}
        >
          {rank}
        </Typography>
      )}

      {/* 2. 이미지 + 정보 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, md: 2 },
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* 이미지 */}
        <Link to={albumUrl} onClick={handleAlbumClick} style={{ display: 'block', flexShrink: 0 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={albumTitle}
            onError={handleError}
            sx={{
              width: { xs: 64, md: 80 },
              height: { xs: 64, md: 80 },
              display: 'block',
            }}
          />
        </Link>

        {/* 정보 */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* 제목 + 노래장르 아이콘 */}
          <AlbumTitleLink
            title={albumTitle}
            to={albumUrl}
            onClick={trackAlbumClick}
            contentRating={contentRating}
            sx={{
              fontSize: { xs: 13, md: 14 },
            }}
            iconSx={{
              width: { xs: 12, md: 14 },
              height: { xs: 12, md: 14 },
            }}
          />

          {/* 아티스트 */}
          {artistName && (
            <ArtistLink
              artistName={artistName}
              artistSource={artistSource}
              artistSourceId={artistSourceId}
              artistSlug={artistSlug}
              artistDbId={artistDbId}
              onClick={handleArtistClick}
              sx={{
                mt: 1,
                fontSize: { xs: 12, md: 14 },
                color: theme.palette.black[4],
                WebkitLineClamp: 2,
              }}
            />
          )}

          {/* 장르 */}
          {genreNames.length > 0 && (
            <Typography
              sx={{
                mt: 1,
                fontSize: { xs: 12, md: 13 },
                color: theme.palette.black[4],
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: 'normal',
              }}
              title={genreNames.join(', ')}
            >
              {genreNames.join(', ')}
            </Typography>
          )}

          {/* 발매일 */}
          {releaseDate && (
            <Typography
              sx={{
                mt: { xs: 1.5, md: 2 },
                fontSize: { xs: 12, md: 14 },
                color: theme.palette.black[4],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 'normal',
              }}
              title={releaseDate}
            >
              {releaseDate}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlaylistAlbumCard;
